import {
	AFFIRM_CORE,
	AFFIRM_FILLERS,
	AFFIRM_MAX_TOKENS,
	BRING_VERB_PATTERN,
	DI_WORD,
	DISPLAY_NOUNS,
	DISPLAY_VERB_PATTERNS,
	GIVE_WORDS,
	LEADING_FILLERS,
	MANA_WORD,
	NON_REQUEST_PRECEDERS,
	OFFER_TAIL_CHARS,
	OPEN_VERB_PATTERN,
	SEE_WORDS,
	TO_WORD,
} from "./display-intent.words";

/** Detektor niat "tampilkan/tunjukkan" — murni (tanpa I/O), dipakai server untuk menunjuk otomatis (#63). */

const NOUN_WINDOW = 3;
const MODULE_WINDOW = 3;
const SENTENCE_BREAK = /(?<=[.!?])\s+/;

/** Huruf kecil, tanpa aksen/tanda baca; huruf berulang ≥3 diringkas ("tunjuukkin" → "tunjukin"). */
export function tokenize(text: string): string[] {
	return text
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/(.)\1{2,}/g, "$1")
		.replace(/[^a-z0-9]+/g, " ")
		.trim()
		.split(" ")
		.filter(Boolean);
}

/** Buang akhiran "-nya" untuk pencocokan kata benda ("halamannya" → "halaman"). */
export function stripNya(token: string): string {
	return token.length > 5 && token.endsWith("nya") ? token.slice(0, -3) : token;
}

function isNoun(token: string | undefined): boolean {
	return token !== undefined && DISPLAY_NOUNS.has(stripNya(token));
}

function nounWithin(tokens: readonly string[], from: number): boolean {
	return tokens.slice(from, from + NOUN_WINDOW).some(isNoun);
}

/** Kata kerja tampil yang berdiri sendiri (tanpa syarat konteks). */
function isDisplayVerb(token: string): boolean {
	return DISPLAY_VERB_PATTERNS.some((p) => p.test(token));
}

export interface DisplayIntent {
	hit: boolean;
	/** Kata/pola pemicu pertama (untuk log & debug). */
	trigger?: string;
}

function intentAt(tokens: readonly string[], i: number): string | undefined {
	const t = tokens[i] ?? "";
	const next = tokens[i + 1];
	if (isDisplayVerb(t)) return t;
	if (t === DI_WORD && next === MANA_WORD) return "di mana";
	if (OPEN_VERB_PATTERN.test(t) && nounWithin(tokens, i + 1)) return `${t} …`;
	if (
		BRING_VERB_PATTERN.test(t) &&
		tokens.slice(i + 1, i + 1 + MODULE_WINDOW).includes(TO_WORD)
	)
		return `${t} ke`;
	if (SEE_WORDS.has(t) && nounWithin(tokens, i + 1)) return `${t} <tampilan>`;
	if (GIVE_WORDS.has(t) && next !== undefined && SEE_WORDS.has(next))
		return `${t} ${next}`;
	if (
		t === MANA_WORD &&
		isNoun(next) &&
		tokens.slice(0, i).every((p) => LEADING_FILLERS.has(p))
	)
		return "mana <tampilan>";
	return undefined;
}

/** True bila kalimat berisi permintaan eksplisit untuk menampilkan/menunjukkan sesuatu di layar. */
export function detectDisplayIntent(text: string): DisplayIntent {
	const tokens = tokenize(text);
	for (let i = 0; i < tokens.length; i++) {
		const trigger = intentAt(tokens, i);
		if (!trigger) continue;
		const prev = tokens[i - 1];
		if (prev !== undefined && NON_REQUEST_PRECEDERS.has(prev)) continue;
		return { hit: true, trigger };
	}
	return { hit: false };
}

/** Balasan setuju singkat ("iya", "boleh dong", "oke silakan") — hanya bermakna setelah penawaran. */
export function isShortAffirmation(text: string): boolean {
	const tokens = tokenize(text);
	if (tokens.length === 0 || tokens.length > AFFIRM_MAX_TOKENS) return false;
	return (
		tokens.every((t) => AFFIRM_CORE.has(t) || AFFIRM_FILLERS.has(t)) &&
		tokens.some((t) => AFFIRM_CORE.has(t))
	);
}

/** True bila jawaban asisten berakhir dengan tawaran menunjuk ("Mau saya tunjukkan di layar?"). */
export function endsWithPointOffer(answer: string): boolean {
	const tail = answer.trim().slice(-OFFER_TAIL_CHARS);
	return tail.includes("?") && detectDisplayIntent(tail).hit;
}

/** Buang kalimat tawaran di ujung jawaban bila layar sudah menunjuk (sisanya dipertahankan). */
export function stripPointOffer(answer: string): string {
	const sentences = answer.trim().split(SENTENCE_BREAK);
	const last = sentences.at(-1) ?? "";
	if (sentences.length < 2 || !last.trim().endsWith("?")) return answer;
	return detectDisplayIntent(last).hit
		? sentences.slice(0, -1).join(" ")
		: answer;
}
